import React, { createContext, memo, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import CONFIG from '../config/config';

const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

interface StripeContextType {
  stripePromise: Promise<Stripe | null> | null;
  isIframeEnvironment: boolean;
}

export const StripeContext = createContext<StripeContextType>({
  stripePromise: null,
  isIframeEnvironment: false,
});

// Helper function to detect if running in iframe
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // If we can't access window.top due to cross-origin, we're likely in an iframe
  }
};

export const StripeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isIframeEnvironment] = useState(isInIframe());

  // Only load Stripe if not in iframe environment
  const [stripePromise] = useState(() => {
    if (isIframeEnvironment) {
      if (enableDevTools) console.log('Stripe: Skipping load in iframe environment');
      return null;
    }

    return loadStripe(CONFIG.STRIPE_PUBLISHABLE_KEY);
  });

  return (
    <StripeContext.Provider
      value={{
        stripePromise,
        isIframeEnvironment,
      }}
    >
      {children}
    </StripeContext.Provider>
  );
};

export default memo(StripeProvider);
